/**
 * Shared rules about PDF-based form templates.
 *
 * Both the registration kiosk and the parent portal have to answer the same
 * question — "can this club document actually be filled in on screen?" — and they
 * must answer it identically, or a form fillable at the front desk would silently
 * fall back to read-only at home.
 */

/** The field map, whichever shape the driver handed it back in. */
export function parseFieldMap(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Browser-fetchable URL for a template's PDF, or null if there isn't one.
 *
 * Only web-relative `uploads/...` paths are reachable — that is what
 * express.static('/uploads') serves. Some older rows hold an absolute disk path
 * instead, which produces a malformed URL and a broken frame; treating those as
 * "no document" gives an honest message rather than a dead iframe.
 */
export function templatePdfUrl(template, apiBaseUrl = '') {
  const p = template?.pdf_file_path;
  if (!p || typeof p !== 'string') return null;
  if (!/^uploads\//.test(p)) return null;
  return `${apiBaseUrl}/${p}`;
}

/**
 * A PDF template can be filled in on the document itself only when its file is
 * actually fetchable, staff has mapped its blanks, confirmed the map, and marked
 * which field is the signature — without that last part there is nowhere for the
 * drawn signature to land.
 */
export function isFillablePdf(template) {
  if (!templatePdfUrl(template)) return false;
  if (template.field_map_status !== 'confirmed') return false;
  const map = parseFieldMap(template.field_map);
  return map.length > 0 && map.some((f) => f.type === 'signature');
}

const pdfFormTemplate = { parseFieldMap, isFillablePdf, templatePdfUrl };
export default pdfFormTemplate;
